import {
  normalizePlay,
  syncScope,
  type LegacyLocalPlayRecord,
  type SyncRequest,
  type SyncResponse,
} from '@puzzlio/core';
import { LocalStorageKeys } from '@/app/lib/constants';
import { announce, getPlay, hasSynced, hasUnsent, loadScope, saveLocalPlay, scopeStore } from './store';

/*
  When the browser's plays are synced with the server, for each scope a page is using (the cookie player,
  and an embedded player such as Discord's). Two kinds of moment:

  - Ones where changes from elsewhere may be waiting: the page first using a scope, the tab being shown
    again. These always sync (syncNow).
  - Ones that exist to send this browser's changes: finishing or leaving a puzzle, the tab being hidden, the
    page closing. These sync only if something is unsent (sendUnsent).

  Nothing live: a board on screen takes changes from elsewhere only as the tab comes back into view (see
  core's useSyncedPlaySave).

  One sync at a time per scope. A request for another while one runs waits on it, and a follow-up runs only
  if plays were written after the running sync took its push: it pulls anyway, so another pull would learn
  nothing.
*/

/** A sync that hangs is a failure, so whoever waits on it gets an answer. */
const SYNC_TIMEOUT_MS = 15_000;
/** Browsers refuse a keepalive request body past 64 KiB; below this, a sync survives the page closing. */
const KEEPALIVE_LIMIT = 60_000;

/** Each request names its scope, so a sign-in mid-sync can't push one player's plays as another's. */
async function send(scope: string, request: SyncRequest): Promise<SyncResponse> {
  const body = JSON.stringify(request);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);
  try {
    const response = await fetch('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Inkwell-User': scope },
      body,
      keepalive: body.length < KEEPALIVE_LIMIT,
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Sync failed (${response.status})`);
    return (await response.json()) as SyncResponse;
  } finally {
    clearTimeout(timer);
  }
}

const running = new Map<string, Promise<void>>();
const again = new Set<string>();
/** Scopes whose last sync failed, with why. */
const failing = new Map<string, string>();

/** Scopes a page is using, with how many users each has: these sync as the page is hidden and shown. */
const active = new Map<string, number>();
/**
 * Scopes that sync, remembered past their release: a board's last save is sent as it unmounts, which can
 * be after the scope it belonged to was released (an embedded player's, which unmounts with the board).
 */
const syncing = new Set<string>();

export function syncNow(scope: string): Promise<void> {
  if (!syncing.has(scope)) return Promise.resolve();
  const current = running.get(scope);
  if (current) {
    again.add(scope);
    return current;
  }
  const run = (async () => {
    do {
      again.delete(scope);
      try {
        await syncScope(scopeStore(scope), (request) => send(scope, request));
        failing.delete(scope);
      } catch (cause) {
        console.warn('[sync] failed; the local copy stands', cause);
        failing.set(scope, cause instanceof Error ? cause.message : String(cause));
      }
      // Even an unchanged store has news when this was its first sync, or its first failure.
      announce();
    } while (again.has(scope) && hasUnsent(scope));
  })().finally(() => running.delete(scope));
  running.set(scope, run);
  return run;
}

/** Syncs only if this scope has plays waiting to be pushed. */
export function sendUnsent(scope: string): Promise<void> {
  return hasUnsent(scope) ? syncNow(scope) : Promise.resolve();
}

/** The sync of this scope under way, if any, to wait on without asking for another. */
export const syncInFlight = (scope: string): Promise<void> | null => running.get(scope) ?? null;

export const isSyncFailing = (scope: string) => failing.has(scope);

/** Whether a sync of this scope is under way, and why its last one failed if it did. For the admin readout. */
export const syncHealth = (scope: string) => ({ running: running.has(scope), error: failing.get(scope) ?? null });

/**
 * Starts using a scope: reads it from IndexedDB, carries over anything an older version left in
 * localStorage, and syncs it. Returns the release. A scope stays in use while anything holds it.
 */
export function holdScope(scope: string, sync: boolean): { loaded: Promise<void>; release: () => void } {
  active.set(scope, (active.get(scope) ?? 0) + 1);
  if (sync) syncing.add(scope);
  installPageHooks();

  const loaded = loadScope(scope).then(() => {
    migrateLocalPlays(scope);
    void syncNow(scope);
  });
  return {
    loaded,
    release() {
      const users = active.get(scope) ?? 0;
      if (users <= 1) active.delete(scope);
      else active.set(scope, users - 1);
    },
  };
}

/**
 * Resolves once a scope that has never synced has its first sync, or after `timeoutMs`, whichever is first.
 * One that has synced, or doesn't sync, resolves at once.
 */
export function whenSettled(scope: string, timeoutMs: number): Promise<void> {
  if (hasSynced(scope) || !syncing.has(scope)) return Promise.resolve();
  const sync = running.get(scope) ?? syncNow(scope);
  return Promise.race([sync, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

let hooksInstalled = false;

/** Hidden or closing: send what's unsent, if anything. Shown again: pick up other tabs' writes, then sync. */
function installPageHooks() {
  if (hooksInstalled || typeof document === 'undefined') return;
  hooksInstalled = true;

  // After this event's other listeners, so an open board's last save is in what's sent.
  const sendAll = () => queueMicrotask(() => active.forEach((_, scope) => void sendUnsent(scope)));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sendAll();
    else active.forEach((_, scope) => void loadScope(scope).then(() => syncNow(scope)));
  });
  window.addEventListener('pagehide', sendAll);
}

/**
 * Earlier versions kept unsent progress in localStorage, at most 30 plays, each removed once the server had
 * it. Whatever is still there for this scope never reached the server, so it moves into the store as plays
 * waiting to be pushed, and out of localStorage. Records for other ids stay until their scope is used.
 */
function migrateLocalPlays(scope: string) {
  let map: Record<string, LegacyLocalPlayRecord>;
  try {
    const stored = localStorage.getItem(LocalStorageKeys.LOCAL_PLAYS);
    if (!stored) return;
    map = JSON.parse(stored);
  } catch {
    return;
  }

  let moved = false;
  for (const [key, record] of Object.entries(map)) {
    if (record?.userStr !== scope || typeof record.boardId !== 'number') continue;
    moved = true;
    delete map[key];
    // A row the server already has keeps its name, so the push merges into it rather than beside it.
    const cid = record.currentPlayId ? `s:${record.currentPlayId}` : crypto.randomUUID();
    if (getPlay(scope, cid)) continue;

    const savedAt = record.savedAt ?? record.playData?.savedAt ?? Date.now();
    saveLocalPlay(
      scope,
      normalizePlay({
        cid,
        boardId: record.boardId,
        // The server fills these in from the board when it acknowledges the push.
        gameId: null,
        boardDate: null,
        createdAt: savedAt,
        complete: record.complete,
        completedAt: record.complete ? savedAt : null,
        duration: record.playDuration ?? 0,
        playData: { ...record.playData, savedAt },
        moves: record.moveHistory ?? [],
        seq: null,
      })
    );
  }
  if (!moved) return;

  try {
    if (Object.keys(map).length) localStorage.setItem(LocalStorageKeys.LOCAL_PLAYS, JSON.stringify(map));
    else localStorage.removeItem(LocalStorageKeys.LOCAL_PLAYS);
  } catch {
    // The records stay, and the next load skips the ones already carried over (those with a server id).
  }
}

/** Test-only: forget which scopes sync and which are in use. */
export function _resetSyncForTests() {
  active.clear();
  syncing.clear();
  running.clear();
  again.clear();
  failing.clear();
}
